/**
 * Shared searchable select (single + multi).
 *
 * Presentational wrapper over MUI `Autocomplete` that owns the bits every
 * picker in this app used to hand-roll: debounced async search, last-request-
 * wins guarding, loading + "type N chars" messaging, and the label-at-top
 * convention. It knows nothing about FPs / Xero / suppliers - callers supply
 * the data (static `options` OR async `loadOptions`) and the rendering
 * (`getOptionLabel` / `renderOption`).
 *
 * Two option-feed modes:
 *  - `options`     - static / load-once list, filtered client-side.
 *  - `loadOptions` - async server search; this component debounces, gates on
 *                    `minSearchLength`, and applies only the latest response.
 *
 * Single vs multi is the `multiple` flag: `value`/`onChange` are `T | null`
 * for single and `T[]` for multi.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Checkbox,
  CircularProgress,
  TextField,
} from '@mui/material'
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import FilterFieldLabel from '../filters/fields/FilterFieldLabel'
import FieldLabel from '../../admin/shared/fields/FieldLabel'
import { highlightMatch } from '../../../utils/highlightMatch'

type AnyAutocompleteProps = Partial<
  AutocompleteProps<any, boolean, boolean, boolean>
>

export interface SearchSelectProps<T> {
  /** Caption above the control. Omit to render no label. */
  label?: React.ReactNode
  /** Label style: filter caption (default) or heavier admin-form label. */
  context?: 'filter' | 'form'
  /** Append a required marker (form context only). */
  required?: boolean

  /** Multi-select mode. `value`/`onChange` switch to arrays. */
  multiple?: boolean
  /** `T | null` for single; `T[]` for multi. */
  value: T | T[] | null
  /** Receives `T | null` (single) or `T[]` (multi). */
  onChange: (value: any) => void

  /** Static / load-once options (client-filtered). */
  options?: T[]
  /** Async server search. Debounced + latest-response-wins internally. */
  loadOptions?: (query: string) => Promise<T[]>

  getOptionLabel: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  renderOption?: AnyAutocompleteProps['renderOption']
  /** Full input override (e.g. selected-state chips). Default: text + search icon. */
  renderInput?: AnyAutocompleteProps['renderInput']
  /** Ref to the underlying input element (default renderInput only) - e.g. to focus it. */
  inputRef?: React.Ref<HTMLInputElement>

  /**
   * Controlled input text (e.g. a freeSolo code field). When supplied the
   * caller owns the input string via `onInputChange`; otherwise SearchSelect
   * tracks it internally.
   */
  inputValue?: string
  onInputChange?: (value: string) => void

  freeSolo?: boolean
  /**
   * Compact option rows: smaller font + tighter spacing for long lists.
   * Off by default (rows render at the standard size); opt in where space is
   * tight (e.g. the Supplier Purchase Review filters).
   */
  dense?: boolean
  /** Min chars before an async search fires. Default 2 (async) / 0 (static). */
  minSearchLength?: number
  debounceMs?: number
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium'
  /** External loading flag (e.g. while load-once `options` are fetched). */
  loading?: boolean
  noOptionsText?: React.ReactNode
  /** Escape hatch for the long tail of Autocomplete props. */
  autocompleteProps?: AnyAutocompleteProps
  sx?: AnyAutocompleteProps['sx']
}

function SearchSelect<T>({
  label,
  context = 'filter',
  required = false,
  multiple = false,
  value,
  onChange,
  options,
  loadOptions,
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  renderInput,
  inputRef,
  inputValue: controlledInput,
  onInputChange,
  freeSolo = false,
  dense = false,
  minSearchLength,
  debounceMs = 300,
  placeholder,
  disabled = false,
  size = 'small',
  loading: externalLoading = false,
  noOptionsText,
  autocompleteProps,
  sx,
}: SearchSelectProps<T>): React.ReactElement {
  const isAsync = typeof loadOptions === 'function'
  const minLen = minSearchLength ?? (isAsync ? 2 : 0)

  // Typed text, tracked for the min-length message + async trigger. The
  // Autocomplete input itself stays uncontrolled (MUI owns it) unless the
  // caller passes `inputValue` - controlling it always fought MUI's
  // single-select input sync.
  const [query, setQuery] = useState('')
  // Multi-select: we own the input text so the search term (and the filtered
  // list) survives each pick - MUI otherwise clears it on select.
  const [multiInput, setMultiInput] = useState('')
  const persistMulti = multiple && controlledInput === undefined
  const [asyncOptions, setAsyncOptions] = useState<T[]>([])
  const [asyncLoading, setAsyncLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Latest-request-wins: only the newest async response is applied.
  const requestSeq = useRef(0)

  const runSearch = useCallback(
    async (query: string) => {
      if (!loadOptions) return
      const seq = ++requestSeq.current
      setAsyncLoading(true)
      try {
        const results = await loadOptions(query)
        if (seq === requestSeq.current) setAsyncOptions(results)
      } catch {
        if (seq === requestSeq.current) setAsyncOptions([])
      } finally {
        if (seq === requestSeq.current) setAsyncLoading(false)
      }
    },
    [loadOptions],
  )

  const handleInputChange = useCallback(
    (_e: React.SyntheticEvent, next: string, reason: string) => {
      // Multi-select keeps the search term after a pick: MUI fires
      // onInputChange(reason='reset', '') on select - ignore it so the
      // filtered list stays put instead of snapping back to the full list.
      if (persistMulti && reason === 'reset') return
      if (persistMulti) setMultiInput(next)
      setQuery(next)
      onInputChange?.(next)
      if (!isAsync) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (reason === 'reset' || reason === 'clear') return
      if (next.length < minLen) {
        setAsyncOptions([])
        return
      }
      debounceRef.current = setTimeout(() => runSearch(next), debounceMs)
    },
    [persistMulti, isAsync, minLen, debounceMs, runSearch, onInputChange],
  )

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  const loading = externalLoading || asyncLoading
  const effectiveOptions = isAsync ? asyncOptions : options ?? []

  const defaultNoOptionsText =
    isAsync && query.length < minLen
      ? `Type at least ${minLen} character${minLen === 1 ? '' : 's'}`
      : 'No results'

  const defaultRenderInput: AnyAutocompleteProps['renderInput'] = (params) => (
    <TextField
      {...params}
      size={size}
      placeholder={placeholder}
      variant="outlined"
      inputRef={inputRef}
      InputProps={{
        ...params.InputProps,
        startAdornment:
          !multiple && !params.InputProps.startAdornment ? (
            <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          ) : (
            params.InputProps.startAdornment
          ),
        endAdornment: (
          <>
            {loading ? <CircularProgress color="inherit" size={18} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  )

  // Multi-select default: a checkbox per row so the selected state is obvious
  // (the chips below the input only show *what's* picked, not which list rows
  // are selected). Callers can still override with their own renderOption.
  const defaultMultiRenderOption: AnyAutocompleteProps['renderOption'] = (
    props,
    option,
    { selected },
  ) => (
    <li {...props}>
      <Checkbox
        size="small"
        sx={{ mr: 1, p: 0.25 }}
        icon={<CheckBoxBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        checked={selected}
      />
      {/* Wrap in one span: the MUI option <li> is display:flex, so without
          this each highlight fragment becomes a flex item (spaces collapse,
          the <mark> wraps to its own line). */}
      <span>{highlightMatch(getOptionLabel(option as T), query)}</span>
    </li>
  )

  const effectiveRenderOption =
    renderOption ?? (multiple ? defaultMultiRenderOption : undefined)

  const control = (
    <Autocomplete
      multiple={multiple}
      freeSolo={freeSolo}
      disabled={disabled}
      size={size}
      // Multi pickers keep the list open while ticking several rows.
      disableCloseOnSelect={multiple}
      options={effectiveOptions}
      loading={loading}
      value={value as any}
      onChange={(_e, next) => onChange(next)}
      // Control the input text when the caller asked (freeSolo code field) or
      // in multi mode (to persist the search across picks). Single uncontrolled
      // lets MUI own it - controlling it there fought MUI's input sync.
      inputValue={controlledInput ?? (persistMulti ? multiInput : undefined)}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel as any}
      isOptionEqualToValue={isOptionEqualToValue as any}
      renderOption={effectiveRenderOption}
      // Async results are pre-filtered server-side; don't double-filter.
      filterOptions={isAsync ? (x) => x : undefined}
      noOptionsText={noOptionsText ?? defaultNoOptionsText}
      loadingText="Searching..."
      sx={sx}
      // Compact option rows (opt-in) - for long lists where space is tight.
      ListboxProps={
        dense
          ? {
              sx: {
                '& .MuiAutocomplete-option': {
                  fontSize: '0.8125rem',
                  lineHeight: 1.3,
                  minHeight: 'auto',
                  py: 0.5,
                },
              },
            }
          : undefined
      }
      renderInput={renderInput ?? defaultRenderInput}
      {...autocompleteProps}
    />
  )

  if (!label) return control
  return (
    <Box>
      {context === 'form' ? (
        <FieldLabel required={required}>{label}</FieldLabel>
      ) : (
        <FilterFieldLabel>{label}</FilterFieldLabel>
      )}
      {control}
    </Box>
  )
}

export default SearchSelect
